import { useInfiniteQuery, useQuery } from '@tanstack/react-query';

import { createQueryOptions } from '../../../query';
import { RequestMeta } from '../../../temporary/requestmeta.types';
import { useStoreApi } from '../../api';
import { consumerKeys } from '../keys';
import { ParticipantSearch } from './search.types';
import { Participant } from './types';

const useListUserParticipants = ({ queryOptions }: { queryOptions?: ParticipantSearch } = {}) => {
  const StoreApi = useStoreApi();

  const { queryObject, queryString } = createQueryOptions<ParticipantSearch>({
    search: queryOptions,
  });

  const getUserParticipants = async () => {
    const { data } = await StoreApi.get<{ participants: Participant[] }>(
      `/consumer/peer_pages/participants?${queryString}`
    );
    return data.participants;
  };

  const key = [...consumerKeys().participants.all, queryObject];

  return useQuery({
    queryFn: getUserParticipants,
    queryKey: key,
  });
};

const useInfiniteListUserParticipants = ({
  enabled = true,
  queryOptions,
}: {
  enabled?: boolean;
  queryOptions?: ParticipantSearch;
} = {}) => {
  const StoreApi = useStoreApi();

  const getInfiniteUserParticipants = async ({
    pageParam = { limit: 6, offset: 0 },
  }: {
    pageParam: { limit: number; offset: number };
  }) => {
    const { queryString } = createQueryOptions<ParticipantSearch>({
      search: queryOptions,
      pagination: {
        limit: pageParam.limit,
        offset: pageParam.offset,
      },
    });

    const { data } = await StoreApi.get<{ participants: Participant[]; meta: RequestMeta }>(
      `/consumer/peer_pages/participants?${queryString}`
    );
    return data;
  };

  const queryKey = consumerKeys().participants.infinite;

  return useInfiniteQuery({
    queryKey: [...queryKey, queryOptions],
    queryFn: getInfiniteUserParticipants,
    enabled,
    initialPageParam: { limit: 6, offset: 0 },
    getNextPageParam: (lastPage) => {
      if (!lastPage) return undefined;
      const { meta } = lastPage;
      if (!meta) return undefined;
      const { has_more, limit, offset } = meta;
      if (!has_more) {
        return undefined;
      }
      return { limit, offset: offset + limit };
    },
  });
};

export { useInfiniteListUserParticipants, useListUserParticipants };
